import { useEffect, useState, useMemo } from 'react'
import * as Sentry from "@sentry/nextjs";
import _ from 'lodash'

import firebase, { db } from 'utils/firebase'
import { firebaseCollections as collections, rentalStatuses } from 'utils/constants'
import { getUserStoreIds, getNow } from 'utils/helpers'
import { useData } from 'utils/context'
import { useSearch } from 'utils/hooks'

let searchAllQueryLimit = 15
let queryLimit = 15
const makeQuery = ({ status, term, type, sortBy, storeId, statuses, limit, filter }) => {
  const typeValue = type === 'phoneNumberSliced' ? term : term.toLowerCase();
  let query =  db.collection(collections.rentals)
    .where('status', '==', status)
  if (storeId !== 'all') {
    query = query.where('originStoreId', 'in', [storeId])
  }
    
  if (statuses.includes(rentalStatuses.open)) {
    query = filterQuery(query, filter)
  }
  if (term !== '') query = query.where(type, '==', typeValue)
  if (!statuses.includes(rentalStatuses.new)) {
    query = query.limit(limit)
  }
  return query
}

const sortClientSideRentals = (rentals, sortValue) => {
  let ignoreCases = ['', 'none']
  if (rentals.length === 0 && ignoreCases.includes(sortValue)) return rentals
  let sortIteratee = [(rental) => rental[sortValue] || '']
  let sortOrder = 'asc';
  if (sortValue === 'purchaseDate' || sortValue === 'dueDate') {
    sortOrder = 'desc'
  }
  return _.orderBy(rentals, sortIteratee, [sortOrder]);
}

const sortFilteredRentals = (rentals, filter) => {
  let sortIteratee = (key) =>  [(rental) => rental[key] || '']
  if (rentals.length === 0) return rentals
  if (filter === 'today' || filter === 'overdue') {
    return _.orderBy(rentals, sortIteratee('dueDate'), ['asc'])
  }
  return _.orderBy(rentals, sortIteratee('purchaseDate'), ['desc'])
}

const filterQuery = (query, filter) => {
  switch (filter) {
    case 'today': {
      let now = getNow()
      let endOfDay =  firebase.firestore.Timestamp.fromMillis(now.endOf('day').toMillis())
      let startOfDay =  firebase.firestore.Timestamp.fromMillis(now.startOf('day').toMillis())
      return query.where('dueDate', '<=', endOfDay).where('dueDate', '>=', startOfDay).orderBy('dueDate', 'asc')
    }
    case 'overdue': {
      let now =  firebase.firestore.Timestamp.fromMillis(getNow().toMillis())
      return  query.where('dueDate', '<', now).orderBy('dueDate', 'asc')
    }
    default:
      return query.orderBy('purchaseDate', 'desc')
  }
}

export default function useFirestoreRentals ({ statuses, sortBy = 'purchaseDate', pageType }) {
  const search = useSearch()
  const { searchAll = false, currentStore, user, rental } = useData()
  console.log({ searchAll,currentStore, rental })
  const [loading, setLoading] = useState(true)
  const [lastViewed, setLastViewed] = useState({})
  const [hasMore, setHasMore] = useState({})
  const [data, setData] = useState({})
  const [error, ] = useState(null)
  const [fetchCount, setFetchCount] = useState(0)
  const currentStoreId = currentStore?.id ?? ''
  const [sortValue, setSortValue] = useState('')
  const [filter, setFilter] = useState('all')
  const [prevFilter, setPrevFilter] = useState('all')
  const [unsubscribes, setUnscubscribes] = useState([])

  const refetch = () => setFetchCount(count  => count + 1)
/**
 * Remove started rentals from state
 */
  useEffect (() => {
    if (rental) {
      switch (pageType) {
        case rentalStatuses.new: {
          if (rental?.originStoreId && rental.status === rentalStatuses.open) {
            let key = `${rental.originStoreId}-${rentalStatuses.new}`
            let allKey = `all-${rentalStatuses.new}`
            setData(old => {
              const oldData = old[key] ?? []
              return { ...old,
                [key]:oldData.filter(r => r.id !== rental.id )
              }
            })
            setData(old => {
              const allData = old[allKey] ?? []
              return { 
                ...old,
                [allKey]: allData.filter(r => r.id !== rental.id )
              }
            })
          }
        }
        break;
        case rentalStatuses.open: {
          if (rental?.originStoreId && (rental.status === rentalStatuses.closed || rental.status === rentalStatuses.extended)) {
            let key = `${rental.originStoreId}-${rentalStatuses.open}`
            let allKey = `all-${rentalStatuses.open}`
            setData(old => {
              const oldData = old[key] ?? []
              return { 
                ...old, 
                [key]: oldData.filter(r => r.id !== rental.id)
              }
          }
            )
            setData(old => {
              const allData = old[allKey] ?? []
              return { 
                ...old,
                [allKey]: allData.filter(r => r.id !== rental.id )
              }
            })
          }
        }

      }
    }
  }, [rental])

  const results = useMemo(() => {
    let rentals = []
    if(searchAll) {
      const [status] = statuses
      rentals = data[`all-${status}`] ?? []
    } else if (currentStoreId) {
      rentals = statuses.reduce((acc, status) => {
        let result = data[`${currentStoreId}-${status}`] ?? []
        return [
          ...acc, 
          ...result
        ]
      }, [])
    }
    if (pageType === rentalStatuses.open) {
      return sortClientSideRentals(sortFilteredRentals(rentals, filter), sortValue)
    }
    return sortClientSideRentals(rentals, sortValue)
    },
    [data, searchAll, sortValue, filter, search.term]
  )

  const shouldLoadMore = useMemo(() => { 
    const [status] = statuses
    if(searchAll) {
      let allMore = hasMore[`all-${status}`] ?? false
      return allMore
    } else if (currentStoreId) {
      return hasMore[`${currentStoreId}-${status}`] ?? false
    }
    return false
  }, [hasMore])

  useEffect(() => {
    let limit = searchAll ? searchAllQueryLimit : queryLimit
    try {
      let queryStores = searchAll ? ['all'] : currentStoreId ? [currentStoreId] : []

      if (search.previous !== search.term ||  prevFilter !== filter) {
        if (searchAll) {
          setLastViewed({})
          setHasMore({})
          setData({})
        } else if (currentStoreId) {
          statuses.forEach(status => {
            let key = `${currentStoreId}-${status}`
            setLastViewed(old =>  ({ ...old, [key]: null }))
            setData(old =>  ({ ...old, [key]: [] }))
            setHasMore(old =>  {
              let oldData = { ...old }
              if (typeof oldData[key] !== 'undefined') delete oldData[key]
              return oldData
            })
          })
        }
      }
      if (queryStores.length > 0) {
        setLoading(true)
        const [status] = statuses
        const [storeId] = queryStores
        let query = makeQuery({ status, term: search.term, type: search.type, sortBy, storeId, statuses, limit, filter })
        let key = `${storeId}-${status}`
        if (lastViewed[key] && search.previous === search.term &&  prevFilter === filter) {
          query = query.startAfter(lastViewed[key])
        }
        let singleSubscriber = query.onSnapshot((querySnapshot) => {
          let rentals = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      
          const lastIndex = querySnapshot.docs.length - 1
          const lastItem = lastIndex !== -1 ? querySnapshot.docs[lastIndex] : null
          const newHasMore = querySnapshot.docs.length === limit
          /**
           * Only set the last viewed when querySnapshot is empty
           */
          if (lastIndex !== -1) {
            setLastViewed(oldLastviewed =>  ({ ...oldLastviewed, [key]: lastItem }))
          }
          setHasMore(oldHasMore =>  ({ ...oldHasMore, [key]: newHasMore }))
          setData(oldData => {
            let old = oldData[key] ?? []
            let updated = rentals
            /**
             * Only merge the results if there was a change in search term
             * or filter
             */
            if (search.previous === search.term &&  prevFilter === filter) {
              updated = _.uniqBy([ ...rentals, ...old ], 'id')
            }
            return { ...oldData, [key]: updated }
          })
          search.setPrevious(search.term)
          setPrevFilter(filter)
          setLoading(false)
        })
        setUnscubscribes(unsubs => {
          return [...unsubs, singleSubscriber]
        })
      }
    } catch (e) {
      Sentry.captureException(e , {
        tags: {
          action: 'fetch rentals'
        }
      });
      setLoading(false)
    }

    return () => {
    }
  }, [currentStore, fetchCount, searchAll, search.term])
  useEffect(() => {
    if (unsubscribes && unsubscribes.length > 0) {
      unsubscribes.forEach(unsubscribe => unsubscribe())
      setUnscubscribes([])
    }
  }, [])
  return {
    loading,
    data: results,
    error,
    refetch,
    sortValue,
    setSortValue,
    filter,
    setFilter,
    shouldLoadMore
  }
}